import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import {
  CalendarClock,
  ImageIcon,
  Loader2,
  Megaphone,
  Minus,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import {
  ON_DEMAND_CAMPAIGN_MAX_ITEMS,
  STANDALONE_ASSET_BATCH_MAX_ITEMS,
  type OnDemandCampaignCreateRequestV1,
  type StandaloneAssetBatchCreateRequestV1,
} from "@runcheetah/shared";

import { buildBrandPath } from "@/hooks/use-route-brand";
import {
  ON_DEMAND_CAMPAIGN_FORMATS,
  buildOnDemandCampaignCreateRequest,
  buildSuggestedCampaignPrompt,
  createDefaultOnDemandCampaignCounts,
  getOnDemandCampaignCountTotal,
  type OnDemandCampaignPostType,
  updateOnDemandCampaignCount,
} from "@/lib/on-demand-campaigns";
import {
  STANDALONE_ASSET_FORMATS,
  buildStandaloneAssetCreateRequest,
  createEmptyStandaloneAssetCounts,
  getStandaloneAssetCountTotal,
  type StandaloneAssetPostType,
  updateStandaloneAssetCount,
} from "@/lib/standalone-assets";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { RunConfirmationDialog } from "@/components/runs/run-confirmation-dialog";
import { Button, Card, CardContent, Input, Textarea } from "@runcheetah/ui";

const fallbackStandaloneQuoteRequest: StandaloneAssetBatchCreateRequestV1 = {
  version: 1,
  prompt: "Run quote",
  audienceOverride: null,
  items: [
    {
      scheduledFor: new Date(0).toISOString(),
      platform: "instagram",
      postType: "feed_portrait",
      brandPresence: "branded",
    },
  ],
};

const fallbackCampaignQuoteRequest: OnDemandCampaignCreateRequestV1 = {
  version: 1,
  prompt: "Run quote",
  generationMode: "preview_first",
  items: [
    {
      scheduledFor: new Date(0).toISOString(),
      platform: "instagram",
      postType: "feed_portrait",
    },
  ],
};

type SidebarCreateStep = "choice" | "standalone" | "campaign";
type CampaignStep = "brief" | "schedule";

const toInitialDatetimeLocal = (): string => {
  const date = new Date(Date.now() + 24 * 60 * 60 * 1000);
  date.setMinutes(0, 0, 0);
  return date.toISOString().slice(0, 16);
};

export function SidebarCreateModal({
  brandId,
  brandName,
  open,
  onOpenChange,
  onUpgradeRequired,
  upgradeLabel,
}: {
  brandId: string | undefined;
  brandName: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpgradeRequired?: () => void;
  upgradeLabel?: string;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [step, setStep] = useState<SidebarCreateStep>("choice");
  const [campaignStep, setCampaignStep] = useState<CampaignStep>("brief");
  const [standalonePrompt, setStandalonePrompt] = useState("");
  const [standaloneCounts, setStandaloneCounts] = useState(createEmptyStandaloneAssetCounts);
  const [standaloneFirstScheduledFor, setStandaloneFirstScheduledFor] =
    useState(toInitialDatetimeLocal);
  const [pendingStandaloneRequest, setPendingStandaloneRequest] =
    useState<StandaloneAssetBatchCreateRequestV1 | null>(null);
  const [standaloneError, setStandaloneError] = useState<string | null>(null);
  const [campaignPrompt, setCampaignPrompt] = useState("");
  const [campaignCounts, setCampaignCounts] = useState(createDefaultOnDemandCampaignCounts);
  const [campaignFirstScheduledFor, setCampaignFirstScheduledFor] =
    useState(toInitialDatetimeLocal);
  const [pendingCampaignRequest, setPendingCampaignRequest] =
    useState<OnDemandCampaignCreateRequestV1 | null>(null);
  const [campaignError, setCampaignError] = useState<string | null>(null);

  const resetState = () => {
    setStep("choice");
    setCampaignStep("brief");
    setStandalonePrompt("");
    setStandaloneCounts(createEmptyStandaloneAssetCounts());
    setStandaloneFirstScheduledFor(toInitialDatetimeLocal());
    setPendingStandaloneRequest(null);
    setStandaloneError(null);
    setCampaignPrompt("");
    setCampaignCounts(createDefaultOnDemandCampaignCounts());
    setCampaignFirstScheduledFor(toInitialDatetimeLocal());
    setPendingCampaignRequest(null);
    setCampaignError(null);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const createBatch = trpc.standaloneAssetBatch.create.useMutation({
    onSuccess: async () => {
      if (!brandId) {
        return;
      }

      await utils.standaloneAssetBatch.list.invalidate({ brandId });
      await utils.brand.getBillingOverview.invalidate({ brandId });
      onOpenChange(false);
      navigate(buildBrandPath(brandId, "/review"));
    },
    onError: (mutationError) => setStandaloneError(mutationError.message),
  });

  const createCampaign = trpc.campaign.createOnDemand.useMutation({
    onSuccess: () => {
      if (!brandId) {
        return;
      }

      void utils.campaign.list.invalidate({ brandId });
      void utils.campaign.nextGenerationCheck.invalidate({ brandId });
      onOpenChange(false);
      navigate(buildBrandPath(brandId, "/campaigns"), {
        state: { queuedOnDemandCampaignGeneration: true },
      });
    },
    onError: (mutationError) => setCampaignError(mutationError.message),
  });
  const standaloneRunQuoteQuery = trpc.standaloneAssetBatch.quoteCreate.useQuery(
    {
      brandId: brandId ?? "00000000-0000-0000-0000-000000000000",
      request: pendingStandaloneRequest ?? fallbackStandaloneQuoteRequest,
    },
    { enabled: Boolean(brandId && pendingStandaloneRequest), retry: false },
  );
  const campaignRunQuoteQuery = trpc.campaign.quoteCreateOnDemand.useQuery(
    {
      brandId: brandId ?? "00000000-0000-0000-0000-000000000000",
      request: pendingCampaignRequest ?? fallbackCampaignQuoteRequest,
    },
    { enabled: Boolean(brandId && pendingCampaignRequest), retry: false },
  );

  if (!open) {
    return null;
  }

  const standaloneTotal = getStandaloneAssetCountTotal(standaloneCounts);
  const campaignTotal = getOnDemandCampaignCountTotal(campaignCounts);
  const canSubmitStandalone = Boolean(
    brandId && standalonePrompt.trim() && standaloneTotal > 0 && standaloneFirstScheduledFor,
  );
  const canContinueCampaign = Boolean(campaignPrompt.trim());
  const canSubmitCampaign = Boolean(
    brandId && campaignPrompt.trim() && campaignTotal > 0 && campaignFirstScheduledFor,
  );

  const close = () => {
    if (createBatch.isPending || createCampaign.isPending) {
      return;
    }

    onOpenChange(false);
  };

  const backToChoice = () => {
    if (createBatch.isPending || createCampaign.isPending) {
      return;
    }

    setStandaloneError(null);
    setCampaignError(null);
    setCampaignStep("brief");
    setStep("choice");
  };

  const setStandaloneCount = (postType: StandaloneAssetPostType, delta: number) => {
    setStandaloneCounts((current) =>
      updateStandaloneAssetCount({
        counts: current,
        postType,
        delta,
        maxTotal: STANDALONE_ASSET_BATCH_MAX_ITEMS,
      }),
    );
  };

  const setCampaignCount = (postType: OnDemandCampaignPostType, delta: number) => {
    setCampaignCounts((current) =>
      updateOnDemandCampaignCount({
        counts: current,
        postType,
        delta,
        maxTotal: ON_DEMAND_CAMPAIGN_MAX_ITEMS,
      }),
    );
  };

  const submitStandalone = () => {
    setStandaloneError(null);
    if (!brandId) {
      setStandaloneError(
        intl.formatMessage({
          id: "createAssetBatchModal.loadBrandError",
          defaultMessage: "Load your brand before creating assets.",
        }),
      );
      return;
    }

    setPendingStandaloneRequest(
      buildStandaloneAssetCreateRequest({
        prompt: standalonePrompt,
        counts: standaloneCounts,
        firstScheduledFor: standaloneFirstScheduledFor,
        intervalHours: 24,
      }),
    );
  };

  const confirmStandalone = () => {
    if (!brandId || !pendingStandaloneRequest) {
      return;
    }

    createBatch.mutate({ brandId, request: pendingStandaloneRequest });
  };

  const submitCampaign = () => {
    setCampaignError(null);
    if (!brandId) {
      setCampaignError(
        intl.formatMessage({
          id: "sidebarCreateModal.loadBrandErrorCampaign",
          defaultMessage: "Load your brand before creating a campaign.",
        }),
      );
      return;
    }

    setPendingCampaignRequest(
      buildOnDemandCampaignCreateRequest({
        prompt: campaignPrompt,
        counts: campaignCounts,
        firstScheduledFor: campaignFirstScheduledFor,
        intervalHours: 24,
      }),
    );
  };

  const confirmCampaign = () => {
    if (!brandId || !pendingCampaignRequest) {
      return;
    }

    createCampaign.mutate({ brandId, request: pendingCampaignRequest });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/75 p-3 backdrop-blur-sm sm:p-4">
      <Card
        className={cn(
          "max-h-[calc(100dvh-1.5rem)] w-full overflow-y-auto rounded-3xl shadow-xl sm:max-h-[calc(100dvh-2rem)]",
          step === "campaign" ? "max-w-3xl" : "max-w-2xl",
        )}
      >
        {step === "choice" ? (
          <CardContent className="space-y-6 p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  <FormattedMessage id="sidebarCreateModal.badge" defaultMessage="Create new" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight">
                    <FormattedMessage
                      id="sidebarCreateModal.title"
                      defaultMessage="What do you want to create?"
                    />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="sidebarCreateModal.description"
                      defaultMessage="Choose whether you want a coordinated campaign or a batch of standalone assets."
                    />
                  </p>
                </div>
              </div>
              <Button type="button" size="icon" variant="ghost" onClick={close}>
                <X className="h-5 w-5" aria-hidden="true" />
                <span className="sr-only">
                  <FormattedMessage id="common.close" defaultMessage="Close" />
                </span>
              </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setStep("campaign")}
                className="flex min-h-44 flex-col items-start gap-4 rounded-3xl border border-border bg-card px-5 py-5 text-left transition-colors hover:bg-muted/40"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <Megaphone className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="space-y-1">
                  <span className="block text-lg font-semibold text-foreground">
                    <FormattedMessage
                      id="sidebarCreateModal.optionCampaignTitle"
                      defaultMessage="New campaign"
                    />
                  </span>
                  <span className="block text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="sidebarCreateModal.optionCampaignDescription"
                      defaultMessage="Build a strategic multi-post plan around one clear direction."
                    />
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => setStep("standalone")}
                className="flex min-h-44 flex-col items-start gap-4 rounded-3xl border border-border bg-card px-5 py-5 text-left transition-colors hover:bg-muted/40"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted text-foreground">
                  <ImageIcon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="space-y-1">
                  <span className="block text-lg font-semibold text-foreground">
                    <FormattedMessage
                      id="sidebarCreateModal.optionStandaloneTitle"
                      defaultMessage="Standalone assets"
                    />
                  </span>
                  <span className="block text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="sidebarCreateModal.optionStandaloneDescription"
                      defaultMessage="Generate a batch of individual posts without creating a campaign."
                    />
                  </span>
                </span>
              </button>
            </div>
          </CardContent>
        ) : null}

        {step === "standalone" ? (
          <CardContent className="space-y-6 p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  <FormattedMessage
                    id="createAssetBatchModal.badge"
                    defaultMessage="Create assets"
                  />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight">
                    <FormattedMessage
                      id="createAssetBatchModal.title"
                      defaultMessage="Standalone asset batch"
                    />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="createAssetBatchModal.description"
                      defaultMessage="Brief Cheetah once, pick supported Instagram formats, and set the first planned slot."
                    />
                  </p>
                </div>
              </div>
              <Button type="button" size="icon" variant="ghost" onClick={close}>
                <X className="h-5 w-5" aria-hidden="true" />
                <span className="sr-only">
                  <FormattedMessage id="common.close" defaultMessage="Close" />
                </span>
              </Button>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium" htmlFor="sidebar-standalone-asset-prompt">
                <FormattedMessage
                  id="createAssetBatchModal.promptLabel"
                  defaultMessage="What should these posts be about?"
                />
              </label>
              <Textarea
                id="sidebar-standalone-asset-prompt"
                value={standalonePrompt}
                onChange={(event) => setStandalonePrompt(event.target.value)}
                rows={5}
                placeholder={intl.formatMessage({
                  id: "createAssetBatchModal.promptPlaceholder",
                  defaultMessage:
                    "Create a warm batch of standalone posts that make our product feel practical and personal.",
                })}
              />
            </div>

            <div className="rounded-2xl border">
              <div className="grid grid-cols-[1fr_auto] border-b px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>
                  <FormattedMessage id="common.format" defaultMessage="Format" />
                </span>
                <span>
                  <FormattedMessage id="common.count" defaultMessage="Count" />
                </span>
              </div>
              {STANDALONE_ASSET_FORMATS.map((format) => (
                <div
                  key={format.postType}
                  className="grid grid-cols-[1fr_auto] items-center gap-4 border-b px-4 py-4 last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                      <ImageIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="font-medium">{intl.formatMessage(format.label)}</div>
                      <div className="text-sm text-muted-foreground">
                        {intl.formatMessage(format.description)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => setStandaloneCount(format.postType, -1)}
                      disabled={standaloneCounts[format.postType] === 0}
                    >
                      <Minus className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <div className="w-10 text-center font-semibold">
                      {standaloneCounts[format.postType]}
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => setStandaloneCount(format.postType, 1)}
                      disabled={standaloneTotal >= STANDALONE_ASSET_BATCH_MAX_ITEMS}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="sidebar-standalone-asset-schedule">
                  <FormattedMessage
                    id="createAssetBatchModal.firstPlannedSlot"
                    defaultMessage="First planned slot"
                  />
                </label>
                <Input
                  id="sidebar-standalone-asset-schedule"
                  type="datetime-local"
                  value={standaloneFirstScheduledFor}
                  onChange={(event) => setStandaloneFirstScheduledFor(event.target.value)}
                />
              </div>
              <div className="flex items-center gap-2 rounded-2xl border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                <CalendarClock className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage
                  id="createAssetBatchModal.dailySpacing"
                  defaultMessage="Subsequent assets are spaced daily."
                />
              </div>
            </div>

            {standaloneError ? (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {standaloneError}
              </div>
            ) : null}

            <div className="flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="createAssetBatchModal.selectedCount"
                  defaultMessage="{total} of {max} assets selected"
                  values={{ total: standaloneTotal, max: STANDALONE_ASSET_BATCH_MAX_ITEMS }}
                />
              </p>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={backToChoice}
                  disabled={createBatch.isPending}
                >
                  <FormattedMessage id="sidebarCreateModal.backToChoice" defaultMessage="Back" />
                </Button>
                <Button
                  type="button"
                  onClick={submitStandalone}
                  disabled={!canSubmitStandalone || createBatch.isPending}
                >
                  {createBatch.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  <FormattedMessage
                    id="createAssetBatchModal.generateBatch"
                    defaultMessage="Generate batch"
                  />
                </Button>
              </div>
            </div>
          </CardContent>
        ) : null}

        {step === "campaign" ? (
          <CardContent className="space-y-6 p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/12 bg-primary/6 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                  <Sparkles className="size-3.5" aria-hidden="true" />
                  <FormattedMessage
                    id="createCampaignModal.badge"
                    defaultMessage="Campaign brief"
                  />
                </div>
                <div className="space-y-2">
                  <h2 className="font-display text-3xl font-bold tracking-tight text-foreground">
                    <FormattedMessage
                      id="createCampaignModal.title"
                      defaultMessage="New campaign"
                    />
                  </h2>
                  <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="createCampaignModal.description"
                      defaultMessage="Turn one strategic direction into a scheduled Instagram campaign."
                    />
                  </p>
                </div>
              </div>
              <Button type="button" size="icon" variant="ghost" onClick={close}>
                <X className="size-5" aria-hidden="true" />
                <span className="sr-only">
                  <FormattedMessage id="common.close" defaultMessage="Close" />
                </span>
              </Button>
            </div>

            <div className="inline-flex w-full rounded-2xl bg-muted/70 p-1 shadow-xs sm:w-fit">
              <button
                type="button"
                onClick={() => setCampaignStep("brief")}
                className={cn(
                  "flex-1 rounded-xl px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors sm:flex-none",
                  campaignStep === "brief" && "bg-card text-primary shadow-sm",
                )}
              >
                <FormattedMessage
                  id="createCampaignModal.step.direction"
                  defaultMessage="1. Direction"
                />
              </button>
              <button
                type="button"
                onClick={() => canContinueCampaign && setCampaignStep("schedule")}
                disabled={!canContinueCampaign}
                className={cn(
                  "flex-1 rounded-xl px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none",
                  campaignStep === "schedule" && "bg-card text-primary shadow-sm",
                )}
              >
                <FormattedMessage
                  id="createCampaignModal.step.mixSchedule"
                  defaultMessage="2. Mix & schedule"
                />
              </button>
            </div>

            {campaignStep === "brief" ? (
              <div className="space-y-5">
                <div className="rounded-3xl border border-border/70 bg-muted/25 p-5 sm:p-6">
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-foreground">
                      <FormattedMessage
                        id="createCampaignModal.promptLabel"
                        defaultMessage="What should Cheetah plan?"
                      />
                    </div>
                    <p className="text-sm leading-6 text-muted-foreground">
                      <FormattedMessage
                        id="createCampaignModal.promptSupport"
                        defaultMessage="Describe the angle, audience, or proof points you want the campaign to carry across its posts."
                      />
                    </p>
                  </div>
                  <Textarea
                    id="sidebar-campaign-prompt"
                    value={campaignPrompt}
                    onChange={(event) => setCampaignPrompt(event.target.value)}
                    rows={8}
                    className="mt-4 min-h-[12rem] rounded-3xl border-border/70 bg-background/85 px-4 py-3 shadow-none"
                    placeholder={intl.formatMessage({
                      id: "createCampaignModal.promptPlaceholder",
                      defaultMessage:
                        "Create a campaign that introduces a new product angle, proves the value with customer evidence, and ends with a clear next step.",
                    })}
                  />
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full border-border/80 bg-background/80"
                      onClick={() =>
                        setCampaignPrompt(buildSuggestedCampaignPrompt(intl, brandName))
                      }
                    >
                      <FormattedMessage
                        id="createCampaignModal.useSuggestedBrief"
                        defaultMessage="Use suggested brief"
                      />
                    </Button>
                    <div className="text-xs text-muted-foreground">
                      <FormattedMessage
                        id="createCampaignModal.briefHint"
                        defaultMessage="A clear brief unlocks a stronger mix in the next step."
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="rounded-3xl border border-border/70 bg-card/90 p-1">
                  <div className="grid grid-cols-[1fr_auto] px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    <span>
                      <FormattedMessage
                        id="createCampaignModal.instagramFormat"
                        defaultMessage="Instagram format"
                      />
                    </span>
                    <span>
                      <FormattedMessage id="common.count" defaultMessage="Count" />
                    </span>
                  </div>
                  <div className="space-y-2">
                    {ON_DEMAND_CAMPAIGN_FORMATS.map((format) => (
                      <div
                        key={format.postType}
                        className="grid grid-cols-[1fr_auto] items-center gap-4 rounded-2xl border border-transparent px-4 py-4 transition-colors hover:border-accent/20 hover:bg-muted/35"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex size-11 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                            <ImageIcon className="size-5" aria-hidden="true" />
                          </div>
                          <div>
                            <div className="font-semibold text-foreground">
                              {intl.formatMessage(format.label)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {intl.formatMessage(format.description)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="rounded-xl border-border/80 bg-background/80"
                            onClick={() => setCampaignCount(format.postType, -1)}
                            disabled={campaignCounts[format.postType] === 0}
                          >
                            <Minus className="size-4" aria-hidden="true" />
                          </Button>
                          <div className="w-10 text-center font-semibold tabular-nums text-foreground">
                            {campaignCounts[format.postType]}
                          </div>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="rounded-xl border-border/80 bg-background/80"
                            onClick={() => setCampaignCount(format.postType, 1)}
                            disabled={campaignTotal >= ON_DEMAND_CAMPAIGN_MAX_ITEMS}
                          >
                            <Plus className="size-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
                  <div className="rounded-3xl border border-border/70 bg-muted/25 p-5 sm:p-6">
                    <label
                      className="text-sm font-semibold text-foreground"
                      htmlFor="sidebar-campaign-schedule"
                    >
                      <FormattedMessage
                        id="createCampaignModal.firstPlannedSlot"
                        defaultMessage="First planned slot"
                      />
                    </label>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      <FormattedMessage
                        id="createCampaignModal.scheduleSupport"
                        defaultMessage="Pick the first publication time and Cheetah will spread the rest of the campaign from there."
                      />
                    </p>
                    <Input
                      id="sidebar-campaign-schedule"
                      type="datetime-local"
                      value={campaignFirstScheduledFor}
                      onChange={(event) => setCampaignFirstScheduledFor(event.target.value)}
                      className="mt-4 rounded-2xl border-border/80 bg-background/85"
                    />
                  </div>
                  <div className="rounded-3xl border border-primary/12 bg-primary/6 p-5 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                      <CalendarClock className="size-4" aria-hidden="true" />
                      <FormattedMessage
                        id="createCampaignModal.scheduleCardTitle"
                        defaultMessage="Scheduling rhythm"
                      />
                    </div>
                    <p className="mt-3 leading-6">
                      <FormattedMessage
                        id="createCampaignModal.dailySpacing"
                        defaultMessage="Campaign posts are spaced daily."
                      />
                    </p>
                  </div>
                </div>
              </div>
            )}

            {campaignError ? (
              <div className="space-y-3 rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <div>{campaignError}</div>
                {onUpgradeRequired ? (
                  <Button type="button" variant="outline" size="sm" onClick={onUpgradeRequired}>
                    <FormattedMessage
                      id="runConfirmation.upgrade"
                      defaultMessage="Upgrade to keep creating"
                    />
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-col gap-3 border-t border-border/70 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="createCampaignModal.selectedCount"
                  defaultMessage="{total} of {max} campaign posts selected"
                  values={{ total: campaignTotal, max: ON_DEMAND_CAMPAIGN_MAX_ITEMS }}
                />
              </p>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                {campaignStep === "schedule" ? (
                  <Button type="button" variant="outline" onClick={() => setCampaignStep("brief")}>
                    <FormattedMessage id="common.back" defaultMessage="Back" />
                  </Button>
                ) : (
                  <Button type="button" variant="outline" onClick={backToChoice}>
                    <FormattedMessage id="sidebarCreateModal.backToChoice" defaultMessage="Back" />
                  </Button>
                )}
                {campaignStep === "brief" ? (
                  <Button
                    type="button"
                    onClick={() => setCampaignStep("schedule")}
                    disabled={!canContinueCampaign}
                  >
                    <FormattedMessage id="common.continue" defaultMessage="Continue" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={submitCampaign}
                    disabled={!canSubmitCampaign || createCampaign.isPending}
                  >
                    {createCampaign.isPending ? (
                      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    ) : null}
                    <FormattedMessage
                      id="createCampaignModal.generateCampaign"
                      defaultMessage="Generate campaign"
                    />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        ) : null}

        <RunConfirmationDialog
          open={Boolean(pendingStandaloneRequest)}
          title={intl.formatMessage({
            id: "createAssetBatchModal.runConfirmTitle",
            defaultMessage: "Use runs to generate this batch?",
          })}
          description={intl.formatMessage({
            id: "createAssetBatchModal.runConfirmDescription",
            defaultMessage: "Cheetah will start generating the selected standalone posts.",
          })}
          confirmLabel={intl.formatMessage({
            id: "createAssetBatchModal.runConfirmAction",
            defaultMessage: "Generate and use runs",
          })}
          upgradeLabel={upgradeLabel}
          quote={standaloneRunQuoteQuery.data ?? null}
          quotePending={standaloneRunQuoteQuery.isPending || standaloneRunQuoteQuery.isFetching}
          confirmPending={createBatch.isPending}
          error={standaloneError ?? standaloneRunQuoteQuery.error?.message ?? null}
          onCancel={() => {
            if (!createBatch.isPending) {
              setPendingStandaloneRequest(null);
            }
          }}
          onUpgrade={onUpgradeRequired}
          onConfirm={confirmStandalone}
        />
        <RunConfirmationDialog
          open={Boolean(pendingCampaignRequest)}
          title={intl.formatMessage({
            id: "createCampaignModal.runConfirmTitle",
            defaultMessage: "Use runs to generate this campaign?",
          })}
          description={intl.formatMessage({
            id: "createCampaignModal.runConfirmDescription",
            defaultMessage:
              "Cheetah will generate one campaign preview now. Approving the campaign later will use runs for the remaining posts.",
          })}
          confirmLabel={intl.formatMessage({
            id: "createCampaignModal.runConfirmAction",
            defaultMessage: "Generate preview and use runs",
          })}
          upgradeLabel={upgradeLabel}
          quote={campaignRunQuoteQuery.data ?? null}
          quotePending={campaignRunQuoteQuery.isPending || campaignRunQuoteQuery.isFetching}
          confirmPending={createCampaign.isPending}
          error={campaignError ?? campaignRunQuoteQuery.error?.message ?? null}
          onCancel={() => {
            if (!createCampaign.isPending) {
              setPendingCampaignRequest(null);
            }
          }}
          onUpgrade={onUpgradeRequired}
          onConfirm={confirmCampaign}
        />
      </Card>
    </div>
  );
}
